<script setup lang="ts">
import { computed } from 'vue'
import { METER_TONE_FILL, type MeterPart, type MeterTone } from './app-meter-types'

const props = withDefaults(
  defineProps<{
    /** Accessible name. With `parts` it is the whole reading, e.g. "12 of 20 keywords with content". */
    label: string
    /** One-part form: completion from 0 to 1, clamped; the meter is a `progressbar`. */
    value?: number
    tone?: MeterTone
    /** Many-part form: the filled stretches, in order. Wins over `value`. */
    parts?: MeterPart[]
    /** Many-part form: what the parts are counted out of. */
    total?: number
    /** Legend words for what is left unfilled, e.g. "no content yet". */
    restLabel?: string
    /** Shows the legend under the channel: `row` wraps, `column` stacks (as inside a metric cell). */
    legend?: 'row' | 'column'
    /** Many-part form: text set right after each legend count, such as `%` when the parts are shares. */
    legendSuffix?: string
  }>(),
  {
    value: 0,
    tone: 'accent',
    parts: undefined,
    total: 0,
    restLabel: undefined,
    legend: undefined,
    legendSuffix: '',
  },
)

const percent = computed(() => Math.round(Math.min(1, Math.max(0, props.value)) * 100))

const drawn = computed(() => {
  if (!props.parts) return [{ key: 'value', tone: props.tone, width: percent.value }]
  const total = props.total
  return props.parts.map((part) => ({
    key: part.key,
    tone: part.tone,
    width: total > 0 ? (Math.max(0, part.value) / total) * 100 : 0,
  }))
})

const legendRows = computed(() => {
  if (!props.parts || !props.legend) return []
  const rows = props.parts
    .filter((part) => part.label && part.value > 0)
    .map((part) => ({
      key: part.key,
      fill: METER_TONE_FILL[part.tone],
      count: part.value,
      label: part.label!,
    }))
  const filled = props.parts.reduce((sum, part) => sum + Math.max(0, part.value), 0)
  const rest = props.total - filled
  if (props.restLabel && rest > 0) {
    rows.push({
      key: 'rest',
      fill: 'bg-bg-tertiary inset-shadow-press',
      count: rest,
      label: props.restLabel,
    })
  }
  return rows
})
</script>

<template>
  <div class="flex w-full min-w-0 flex-col gap-1">
    <span
      class="relative flex h-4 w-full overflow-hidden rounded-full bg-bg-tertiary"
      v-bind="
        parts
          ? { role: 'img', 'aria-label': label }
          : {
              role: 'progressbar',
              'aria-label': label,
              'aria-valuenow': percent,
              'aria-valuemin': 0,
              'aria-valuemax': 100,
            }
      "
    >
      <span
        v-for="part in drawn"
        :key="part.key"
        class="block h-full shrink-0 transition-[width] duration-slow ease-soft motion-reduce:transition-none"
        :class="METER_TONE_FILL[part.tone]"
        :style="{ width: `${part.width}%` }"
      />
      <span class="pointer-events-none absolute inset-0 rounded-full inset-shadow-press" />
    </span>
    <ul
      v-if="legendRows.length > 0"
      class="mt-4 flex gap-x-8 gap-y-2"
      :class="legend === 'column' ? 'flex-col' : 'flex-wrap'"
    >
      <li
        v-for="row in legendRows"
        :key="row.key"
        class="inline-flex items-center gap-2 text-caption text-text-secondary"
      >
        <span class="size-4 shrink-0 rounded-full" :class="row.fill" />
        <span
          ><span class="tabular-nums text-text-primary">{{ row.count }}{{ legendSuffix }}</span>
          {{ row.label }}</span
        >
      </li>
    </ul>
  </div>
</template>
